import React, { FC, useRef, useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { FileUpload, FileUploadSelectEvent } from "primereact/fileupload";
import { Image } from "primereact/image";
import { toast } from "react-toastify";
import { toastOptObj } from "../../../utils/constants";

interface UploadTBSImagesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (
        zoomImage: File | null,
        packingImage: File | null
    ) => Promise<void>;
}

interface ImageFile {
    fileName: string;
    file: File;
}
const UploadTBSImagesModal: FC<UploadTBSImagesModalProps> = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const zoomRef = useRef<FileUpload>(null);
    const packingRef = useRef<FileUpload>(null);

    const [zoomImage, setZoomImage] = useState<ImageFile | null>(null);
    const [packingImage, setPackingImage] = useState<ImageFile | null>(null);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen) {
            setZoomImage(null);
            setPackingImage(null);
            setIsSubmitted(false);

            zoomRef.current?.clear();
            packingRef.current?.clear();
        }
    }, [isOpen]);
    const handleClose = () => {
        setZoomImage(null);
        setPackingImage(null);
        zoomRef.current?.clear();
        packingRef.current?.clear();
        onClose();
    };
    const validateImage = (file: File): boolean => {
        const fileType = file.type.split("/")[1]?.toLowerCase();
        const maxFileSize = 25 * 1024 * 1024;
        if (!["jpg", "jpeg", "png", "webp"].includes(fileType)) {
            toast.error("Only JPG, JPEG, PNG and WEBP files are allowed.", {
                ...toastOptObj,
            });
            return false;
        }
        if (file.size > maxFileSize) {
            toast.error("Maximum file size is 25MB.", {
                ...toastOptObj,
            });
            return false;
        }

        return true;
    };

    const onZoomUpload = (e: FileUploadSelectEvent) => {
        const file = e.files?.[0];

        if (!file) return;

        if (!validateImage(file)) {
            zoomRef.current?.clear();
            return;
        }

        setZoomImage({
            file,
            fileName: file.name,
        });

        zoomRef.current?.clear();
    };

    const onPackingUpload = (e: FileUploadSelectEvent) => {
        const file = e.files?.[0];

        if (!file) return;

        if (!validateImage(file)) {
            packingRef.current?.clear();
            return;
        }

        setPackingImage({
            file,
            fileName: file.name,
        });

        packingRef.current?.clear();
    };

    return (
        <Dialog
            visible={isOpen}
            onHide={handleClose}
            draggable={false}
            resizable={false}
            style={{ width: "650px" }}
            header={<h5 className="mb-0">Upload Files</h5>}
        >
            <div className="row">
                <div className="col-md-6">
                    <div className="thumbnail-box">
                        <img
                            className="mt-3"
                            src="/assets/images/upload-file.svg"
                            alt="upload"
                        />

                        <label className="mt-3 form-label">
                            Zoom Picture <sup className="mandatory">*</sup>
                        </label>

                        <FileUpload
                            ref={zoomRef}
                            mode="basic"
                            accept="image/*"
                            onSelect={onZoomUpload}
                            auto
                            customUpload
                            className="p-fileupload-custom"
                            chooseOptions={{
                                label: "Select File",
                                icon: "",
                                className: "mt-3 custom-select-file-btn",
                            }}
                        />
                    </div>

                    <span style={{ color: "#909090", fontSize: "12px" }}>
                        Allowed file types: JPG, JPEG, PNG, WEBP
                    </span>
                    <br />
                    <span style={{ color: "#909090", fontSize: "12px" }}>
                        Max File Size: 25MB
                    </span>
                    <br />
                    <span style={{ color: "#909090", fontSize: "12px" }}>
                        This image will be displayed on the Estimate page.
                    </span>

                    {zoomImage && (
                        <div className="file-card position-relative mt-3 d-flex flex-column align-items-center justify-content-between">
                            <div
                                className="file-card-delete"
                                onClick={() => setZoomImage(null)}
                            >
                                <span className="file-card-delete-icon">×</span>
                            </div>

                            <div className="d-flex align-items-center justify-content-center flex-grow-1 file-card-preview">
                                <Image
                                    src={URL.createObjectURL(zoomImage.file)}
                                    zoomSrc={URL.createObjectURL(zoomImage.file)}
                                    preview
                                    width="130"
                                    height="100"
                                    alt="Zoom Picture"
                                    className="file-card-image"
                                />
                            </div>

                            <div
                                className="file-card-footer text-center text-truncate w-100"
                                title={zoomImage.fileName}
                            >
                                {zoomImage.fileName}
                            </div>
                        </div>
                    )}
                </div>
                <div className="col-md-6">
                    <div className="thumbnail-box">
                        <img
                            className="mt-3"
                            src="/assets/images/upload-file.svg"
                            alt="upload"
                        />

                        <label className="mt-3 form-label">
                            Packing Picture <sup className="mandatory">*</sup>
                        </label>

                        <FileUpload
                            ref={packingRef}
                            mode="basic"
                            accept="image/*"
                            onSelect={onPackingUpload}
                            auto
                            customUpload
                            className="p-fileupload-custom"
                            chooseOptions={{
                                label: "Select File",
                                icon: "",
                                className: "mt-3 custom-select-file-btn",
                            }}
                        />
                    </div>

                    <span style={{ color: "#909090", fontSize: "12px" }}>
                        Allowed file types: JPG, JPEG, PNG, WEBP
                    </span>
                    <br />
                    <span style={{ color: "#909090", fontSize: "12px" }}>
                        Max File Size: 25MB
                    </span>

                    {packingImage && (
                        <div className="file-card position-relative mt-3 d-flex flex-column align-items-center justify-content-between">
                            <div
                                className="file-card-delete"
                                onClick={() => setPackingImage(null)}
                            >
                                <span className="file-card-delete-icon">×</span>
                            </div>

                            <div className="d-flex align-items-center justify-content-center flex-grow-1 file-card-preview">
                                <Image
                                    src={URL.createObjectURL(packingImage.file)}
                                    zoomSrc={URL.createObjectURL(packingImage.file)}
                                    preview
                                    width="130"
                                    height="100"
                                    alt="Packing Picture"
                                    className="file-card-image"
                                />
                            </div>

                            <div
                                className="file-card-footer text-center text-truncate w-100"
                                title={packingImage.fileName}
                            >
                                {packingImage.fileName}
                            </div>
                        </div>
                    )}
                </div>

                {isSubmitted && (!zoomImage || !packingImage) && (
                    <div className="col-12">
                        <span className="text-danger">
                            Please upload both images.
                        </span>
                    </div>
                )}
            </div>

            <div className="d-flex gap-2 mt-4">
                <button
                    className="btn btn-secondary w-100"
                    onClick={handleClose}
                >
                    Close
                </button>

                <button
                    className="btn btn-primary w-100"
                    onClick={async () => {
                        setIsSubmitted(true);
                        if (!zoomImage || !packingImage) {
                            return;
                        }
                        await onSave(
                            zoomImage.file,
                            packingImage.file
                        );
                    }}
                >
                    Upload
                </button>
            </div>
        </Dialog>
    );
};

export default UploadTBSImagesModal;